import React, { useState } from "react";
import * as XLSX from "xlsx";
import { sendEmail } from "../../services/EmailSender";
import MenuBar from "../common/MenuPanel";
import FooterPanel from "../common/FooterPanel";
import { useAuth } from "../../auth/AuthContext";
import emailTemplatesXLS from "../../attachments/GAEmailJSExls.xlsx";

const EmailSenderPage = () => {
  const [contacts, setContacts] = useState([]);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState("");
  const [downloading, setDownloading] = useState(false); // Track download status

  // Function to handle file upload
  const handleFileUpload = (event) => {
    setContacts([]); // Reset contacts when a new file is uploaded  
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        // Add a 'status' field for each contact to track sending status
        setContacts(
          jsonData.map((row) => ({
            ...row,
            emailSentStatus: "Pending",
          }))
        );
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Function to send emails using EmailJS
  const sendEmails = async () => {
    console.log("Sending emails:", contacts);
    if (contacts.length === 0) {
      setEmailStatus("Please upload a file with contacts.");
      return;
    }

    setLoading(true);
    setEmailStatus("Sending emails...");

    try {
      let templateID ;
      let serviceID;
      for (let i = 0; i < contacts.length; i++) {
        const contact = contacts[i];
        templateID = contact.TemplateID;
        serviceID = contact.ServiceID;
        const templateParams = contact;

        const emailResponse = await sendEmail(templateParams, templateID, serviceID);

        // Update contact status based on the response
        setContacts((prevContacts) =>
          prevContacts.map((c, index) =>
            index === i
              ? {
                  ...c,
                  emailSentStatus: emailResponse.success
                    ? "Sent"
                    : emailResponse.error?.status === 422
                    ? `Failed: ${
                        emailResponse.error?.text || "Validation error"
                      }`
                    : "Failed",
                }
              : c
          )
        );
      }

      setEmailStatus("Completed sending emails.");
    } catch (error) {
      console.error("Failed to send email:", error);
      setEmailStatus("Failed to send some emails.");
    } finally {
      setLoading(false);
    }
  };

  // Function to download email status as an Excel file
  const downloadStatus = () => {
    if (contacts.length === 0) {
      setEmailStatus("No contacts to download.");
      return;
    }

    setDownloading(true); // Set downloading state to true

    const ws = XLSX.utils.json_to_sheet(contacts); // Convert contacts to a worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Email Status");

    // Generate Excel file and trigger download
    XLSX.writeFile(wb, "Email_Status.xlsx");

    setDownloading(false); // Reset downloading state after download
  };

  return (
    <React.Fragment>
      <div className="container-fluid position-relative p-0">
        <MenuBar />
      </div>

      {/* Title Section with Overlay */}
      <div className="container-fluid p-0">
        <div
          className="banner-image_sm"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL}/images/2.png)`,
          }}
        >
          <div className="banner-heading text-center wow fadeInDown">
            <h1 className="text-white">Send Emails</h1>
          </div>
        </div>
      </div>

      {/* Render content based on authentication status */}
      <div className="container mt-5">
        <div>
          <p>
            To Send Emails from Excel. Upload Excel with the columns from the
            template -{" "}
          {" "}
          <a href={emailTemplatesXLS} target="_blank" rel="noopener noreferrer">
            Excel Templates.
          </a></p>
        </div>
        <div className="d-flex justify-content-between gap-4 mb-3">
          {/* File Upload */}
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={handleFileUpload}
            disabled={loading}
          />

          {/* Email Sending Button */}
          <button
            onClick={sendEmails}
            disabled={loading || contacts.length === 0}
            className="flex-grow-1"
          >
            {loading ? "Sending..." : "Send Emails"}
          </button>

          {/* Download Status Button */}
          <button
            onClick={downloadStatus}
            disabled={downloading || loading}
            className="flex-grow-1"
          >
            {downloading ? "Downloading..." : "Download Status as Excel"}
          </button>
        </div>

        {/* Status Message */}
        <p>{emailStatus}</p>

        {/* Preview Contacts */}
        <div>
          <p>
            <strong>Contacts and Email Status</strong>
          </p>
          {contacts.length > 0 && (
            <ul>
              {contacts.map((contact, index) => (
                <li key={index}>
                  {contact.to_name||contact.name||contact.fullName} - {contact.to_email||contact.email} - <strong>Status:</strong>{" "}
                  {contact.emailSentStatus}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <FooterPanel />
    </React.Fragment>
  );
};

export default EmailSenderPage;
